import { Link, type LinkProps } from "react-router-dom";
import { useCallback, useRef } from "react";
import { preloadRouteChunk } from "@/lib/preload-routes";

/**
 * N9-v2 — navigation link that pre-fetches the target route's chunk on
 * hover / focus / touchstart.
 *
 * Complements N9 (route-level React.lazy) by hiding the chunk-fetch
 * latency behind the ~200 ms between a user hovering a link and
 * actually clicking it. By the time the click fires, the Suspense
 * boundary no longer needs to suspend — React renders the page
 * instantly.
 *
 * Drop-in replacement for react-router-dom's `<Link>`. Apply in nav
 * surfaces where the next route is part of the 13 code-split pages
 * (see routes-client.tsx). The preload map lives in
 * src/lib/preload-routes.ts and is shared with `<ClickableCard>`.
 */

function resolvePath(to: LinkProps["to"]): string {
  if (typeof to === "string") return to;
  if (to && typeof to === "object" && "pathname" in to && typeof to.pathname === "string") {
    return to.pathname;
  }
  return "";
}

export function PreloadLink(props: LinkProps) {
  // Track whether we already triggered the import for this link so
  // repeated hover/focus events don't cost extra regex matches.
  const triggered = useRef(false);

  const prefetch = useCallback(() => {
    if (triggered.current) return;
    const pathname = resolvePath(props.to);
    if (!pathname) return;
    triggered.current = true;
    preloadRouteChunk(pathname);
  }, [props.to]);

  return (
    <Link
      {...props}
      onMouseEnter={(e) => {
        prefetch();
        props.onMouseEnter?.(e);
      }}
      onFocus={(e) => {
        prefetch();
        props.onFocus?.(e);
      }}
      onTouchStart={(e) => {
        prefetch();
        props.onTouchStart?.(e);
      }}
    />
  );
}
